import { ComponentType, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type React from 'react'
import { Outlet } from 'react-router-dom'
import {
  AccountTree,
  AdminPanelSettings,
  Hub,
  List,
  Lock,
  PermMedia,
  Psychology,
  Settings,
  Map as MapIcon,
} from '@mui/icons-material'
import { Alert, AlertTitle, Button, Collapse } from '@mui/material'
import { com, Nullable } from '@eidu/form'
import { EntityTypesContext } from '../io/context/EntityTypes'
import usePermittedActions from '../io/usePermittedActions'
import useEntitiesOfType from '../io/useEntitiesOfType'
import { useSettings } from '../io/context/Settings'
import SnackBarProvider from '../components/feedback/SnackBarProvider'
import DrawerScaffold, { DrawerItem, DrawerSection } from '../components/layout/DrawerScaffold'
import LoadingOverlay from '../components/feedback/LoadingOverlay'
import { iconForEntityTypeKind } from '../components/misc/entityTypeIcons'
import { isAdminUserType } from '../util/entityType/isAdminUserType'
import { HIERARCHIES_TYPE_NAME, useHierarchiesList } from './hierarchies/hierarchyAccess'
import { ADMIN_SCHEMA_NAME, formatTypeName, getTypeDisplayName } from '../util/entityType/typeNameSchema'
import { isOrderField } from '../util/entityType/orderField'
import { fieldValueToString } from '../domain/entity/DraftFieldValue'
import EntityWithLabelAndRelated from '../domain/entity/EntityWithLabelAndRelated'
import { logMessage } from '../util/Logging'
import Assistent from '../components/assistant/Assistent'
import { findAssetTypeByLabel } from './assets/assetTypes'
import { AssetEntityTypeConfig, useAssetTypeRegistry } from './assets/useAssetTypeRegistry'
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import PermittedGlobalAction = com.eidu.sharedlib.entity.permission.PermittedGlobalAction
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import ReferenceValue = com.eidu.sharedlib.entity.field.ReferenceValue

type IconModule = {
  default: ComponentType
}

const iconModuleImporters = import.meta.glob('/node_modules/@mui/icons-material/esm/*.js') as Record<
  string,
  () => Promise<IconModule>
>

const iconLoaderMap = new Map<string, () => Promise<IconModule>>(
  Object.entries(iconModuleImporters).map(([path, loader]) => {
    const fileName = path.split('/').pop() ?? ''
    const baseName = fileName.replace(/\.[^.]+$/, '')
    return [baseName.toLowerCase(), loader]
  })
)

const iconComponentCache = new Map<string, ComponentType>()
const iconPromiseCache = new Map<string, Promise<ComponentType>>()

const normalizeIconImportName = (iconName?: string) => {
  if (!iconName) return ''
  const cleaned = iconName.trim()
  if (!cleaned) return ''
  return cleaned
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('')
}

const loadMuiIcon = async (iconName: string): Promise<ComponentType> => {
  const normalized = normalizeIconImportName(iconName)
  if (!normalized) throw new Error('Icon name missing')
  const key = normalized.toLowerCase()
  const cachedComponent = iconComponentCache.get(key)
  if (cachedComponent) return cachedComponent

  const cachedPromise = iconPromiseCache.get(key)
  if (cachedPromise) return cachedPromise

  const loader = iconLoaderMap.get(key)
  if (!loader) {
    logMessage(`Navigation icon not found: ${normalized}`, 'warning', 'NavigationIcon')
    throw new Error(`Icon not found: ${normalized}`)
  }

  const promise = loader()
    .then((module) => {
      iconComponentCache.set(key, module.default)
      iconPromiseCache.delete(key)
      return module.default
    })
    .catch((error) => {
      iconPromiseCache.delete(key)
      logMessage(`Failed to load navigation icon ${normalized}: ${String(error)}`, 'warning', 'NavigationIcon')
      throw error
    })

  iconPromiseCache.set(key, promise)
  return promise
}

const DynamicMuiIcon = ({ iconName, fallback }: { iconName?: string; fallback: React.ReactNode }) => {
  const [IconComponent, setIconComponent] = useState<ComponentType | null>(null)

  useEffect(() => {
    let cancelled = false
    setIconComponent(null)
    if (!iconName || normalizeIconImportName(iconName).length === 0) return () => undefined

    loadMuiIcon(iconName)
      .then((component) => {
        if (!cancelled) setIconComponent(() => component)
      })
      .catch(() => {
        if (!cancelled) setIconComponent(null)
      })

    return () => {
      cancelled = true
    }
  }, [iconName])

  if (IconComponent) return <IconComponent />
  return fallback
}

const getFieldString = (values: ReadonlyMap<FieldId, Nullable<FieldValue>>, fieldId?: FieldId) =>
  fieldId ? fieldValueToString(values.get(fieldId) ?? null).trim() : ''

const getAssetTypeLabel = (asset: EntityWithLabelAndRelated, typeFieldId?: FieldId) => {
  if (!typeFieldId) return undefined
  const values = asset.entity.valuesByFieldId.asJsReadonlyMapView()
  const typeValue = values.get(typeFieldId) ?? null
  if (typeValue instanceof ReferenceValue) {
    const referenced = asset.getReferencedEntity(typeValue.value)
    return referenced?.primaryLabelText?.trim()
  }
  return typeValue ? fieldValueToString(typeValue).trim() : undefined
}

const resolveAssetTypeForEntity = (asset: EntityWithLabelAndRelated, assetConfig: AssetEntityTypeConfig) => {
  if (assetConfig.allowedTypes.length === 1 && !assetConfig.typeFieldId) return assetConfig.allowedTypes[0]
  const typeLabel = getAssetTypeLabel(asset, assetConfig.typeFieldId)
  if (!typeLabel) return assetConfig.allowedTypes.length === 1 ? assetConfig.allowedTypes[0] : undefined
  const resolved = findAssetTypeByLabel(typeLabel)
  if (!resolved) return undefined
  return assetConfig.allowedTypeIds.includes(resolved.id) ? resolved : undefined
}

type LandingPageProps = {
  embedMode?: boolean
  overlayNavigation?: boolean
  navigationOpen?: boolean
  onNavigationClose?: () => void
}

const LandingPage = ({
  embedMode = false,
  overlayNavigation = false,
  navigationOpen = false,
  onNavigationClose,
}: LandingPageProps) => {
  if (embedMode) {
    return <Outlet />
  }

  const entityTypeRepository = useContext(EntityTypesContext)
  const { hierarchies, loading: hierarchiesLoading } = useHierarchiesList()
  const { getSettingValue } = useSettings()

  const requested = useRef([
    new PermittedGlobalAction(GlobalAction.ManageForms),
    new PermittedGlobalAction(GlobalAction.ManageEntityTypes),
    new PermittedGlobalAction(GlobalAction.ManagePermissionGrants),
    new PermittedGlobalAction(GlobalAction.ManageAuthentication),
  ])
  const [permissions, fetchPermissionsError] = usePermittedActions(requested.current)

  const entityTypes = entityTypeRepository.useSortedEntityTypes() ?? []
  const entityTypesMap = entityTypeRepository.useAll()

  const error = entityTypeRepository.useError()
  const loading = entityTypeRepository.useLoading()
  const isLoading = loading || hierarchiesLoading

  const hasGlobalPermission = (action: GlobalAction) =>
    permissions?.some((permission) => permission.equals(new PermittedGlobalAction(action)))

  const normalize = (value: string) => value.trim().toLowerCase()
  const { assetEntityTypes, loading: assetRegistryLoading } = useAssetTypeRegistry(entityTypesMap ?? undefined)
  const assetEntityTypeByCategory = useMemo(() => {
    const map = new Map<string, AssetEntityTypeConfig>()
    assetEntityTypes.forEach((config) => {
      map.set(normalize(config.entityTypeName), config)
    })
    return map
  }, [assetEntityTypes, normalize])

  const SETTINGS_TYPE_NAME = formatTypeName(ADMIN_SCHEMA_NAME, 'Settings')
  const NAVIGATION_TYPE_NAME = formatTypeName(ADMIN_SCHEMA_NAME, 'Navigation')
  const ASSET_TYPES_NAME = formatTypeName(ADMIN_SCHEMA_NAME, 'Asset Types')
  const findTypeByName = (types: EntityType[], targetName: string) =>
    types.find((type) => normalize(type.name) === normalize(targetName))

  const createEntityTypeItem = (
    entityType: EntityType,
    icon = iconForEntityTypeKind(entityType.kind, <List />)
  ): DrawerItem => ({
    icon,
    text: getTypeDisplayName(entityType.name),
    key: `type-${entityType.id.toString()}`,
    path: `/entities/${entityType.id.toString()}`,
  })

  const findFieldByName = (type: EntityType, name: string) =>
    type.fields.asJsReadonlyArrayView().find((field) => normalize(field.name) === normalize(name))

  const canManageEntityTypes = hasGlobalPermission(GlobalAction.ManageEntityTypes)
  const canManagePermissionGrants = hasGlobalPermission(GlobalAction.ManagePermissionGrants)
  const canManageAdminUsers = hasGlobalPermission(GlobalAction.ManageAuthentication)

  const adminUsersEntityType = entityTypes.find(
    (type) => type.kind === EntityTypeKind.User && isAdminUserType(type.name)
  )
  const hierarchiesType = findTypeByName(entityTypes, HIERARCHIES_TYPE_NAME)
  const assetTypesType = findTypeByName(entityTypes, ASSET_TYPES_NAME)
  const settingsType = findTypeByName(entityTypes, SETTINGS_TYPE_NAME)
  const navigationType = findTypeByName(entityTypes, NAVIGATION_TYPE_NAME)

  const navigationEntitiesByType = useEntitiesOfType(
    {
      typeIds: navigationType && entityTypesMap ? [navigationType.id] : [],
      allTypes: entityTypesMap ?? new Map(),
      pageSize: 200,
    },
    [navigationType?.id.toString() ?? '', entityTypesMap?.size ?? 0]
  )
  const navigationEntities = navigationType ? navigationEntitiesByType?.get(navigationType.id) : undefined

  type NavigationCategory = 'section' | 'type' | 'hierarchy' | 'asset'
  type NavigationEntry = {
    order: number
    category: NavigationCategory
    label: string
    iconName: string
    assetEntityType?: AssetEntityTypeConfig
  }

  const navigationFields = useMemo(() => {
    if (!navigationType) return null
    const orderField = navigationType.fields.asJsReadonlyArrayView().find((field) => isOrderField(field))
    const categoryField = findFieldByName(navigationType, 'Category')
    const labelField = findFieldByName(navigationType, 'Label')
    const iconField = findFieldByName(navigationType, 'Icon')
    if (!orderField || !categoryField || !labelField) return null
    return { orderField, categoryField, labelField, iconField }
  }, [navigationType])

  const navigationEntries: NavigationEntry[] = useMemo(() => {
    if (!navigationEntities || !navigationFields) return []

    const parseOrder = (value: string) => {
      const parsed = Number.parseFloat(value)
      return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER
    }

    return Array.from(navigationEntities.values())
      .filter((entity): entity is EntityWithLabelAndRelated => entity !== null)
      .flatMap((entity) => {
        const values = entity.entity.valuesByFieldId.asJsReadonlyMapView()
        const rawCategory = normalize(fieldValueToString(values.get(navigationFields.categoryField.id) ?? null))
        const label = fieldValueToString(values.get(navigationFields.labelField.id) ?? null).trim()
        const order = parseOrder(fieldValueToString(values.get(navigationFields.orderField.id) ?? null))
        const iconName =
          navigationFields.iconField && navigationFields.iconField.type === FieldType.Text
            ? fieldValueToString(values.get(navigationFields.iconField.id) ?? null).trim()
            : ''
        let category: NavigationCategory | null = null
        let assetEntityType: AssetEntityTypeConfig | undefined
        if (rawCategory === 'section') category = 'section'
        if (rawCategory === 'type') category = 'type'
        if (rawCategory === 'hierarchy') category = 'hierarchy'
        if (!category) {
          const matchedAssetEntityType = assetEntityTypeByCategory.get(rawCategory)
          if (matchedAssetEntityType) {
            category = 'asset'
            assetEntityType = matchedAssetEntityType
          }
        }
        return category ? [{ category, label, order, iconName, assetEntityType }] : []
      })
      .filter((entry) => entry.label.length > 0)
      .sort((a, b) =>
        a.order === b.order ? a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }) : a.order - b.order
      )
  }, [navigationEntities, navigationFields, normalize, assetEntityTypeByCategory])

  const assetEntityTypeIdsForNavigation = useMemo(() => {
    if (!navigationEntries.length) return []
    const entityTypeIds = new Map<string, AssetEntityTypeConfig['entityTypeId']>()
    navigationEntries.forEach((entry) => {
      if (entry.category === 'asset' && entry.assetEntityType) {
        entityTypeIds.set(entry.assetEntityType.entityTypeId.toString(), entry.assetEntityType.entityTypeId)
      }
    })
    if (entityTypeIds.size === 0) return []
    return Array.from(entityTypeIds.values())
  }, [navigationEntries])

  const assetEntitiesByType = useEntitiesOfType(
    {
      typeIds: assetEntityTypeIdsForNavigation,
      allTypes: entityTypesMap ?? new Map(),
      pageSize: 200,
    },
    [assetEntityTypeIdsForNavigation.map((id) => id.toString()).join('|'), entityTypesMap?.size ?? 0]
  )

  const isAdminUser = canManageEntityTypes || canManagePermissionGrants || canManageAdminUsers

  const adminItems: DrawerItem[] = []
  if (isAdminUser) {
    if (canManageEntityTypes) {
      adminItems.push({
        icon: <Hub />,
        text: 'Types',
        key: 'types',
        path: `/types`,
      })
      if (assetTypesType) {
        adminItems.push({
          icon: <PermMedia />,
          text: getTypeDisplayName(assetTypesType.name),
          key: `asset-types-${assetTypesType.id.toString()}`,
          path: `/entities/${assetTypesType.id.toString()}`,
        })
      }
      if (hierarchiesType) {
        adminItems.push({
          icon: <AccountTree />,
          text: getTypeDisplayName(hierarchiesType.name),
          key: `hierarchies-type-${hierarchiesType.id.toString()}`,
          path: `/entities/${hierarchiesType.id.toString()}`,
        })
      }
      adminItems.push({
        icon: <Psychology />,
        text: 'Semantics',
        key: 'semantics',
        path: `/semantics`,
      })
    }
    if (canManagePermissionGrants) {
      adminItems.push({
        icon: <Lock />,
        text: 'Access Control',
        key: 'access-control',
        path: `/access-control`,
        activePaths: ['/access-control-viewer'],
      })
    }
    if (canManageAdminUsers && adminUsersEntityType) {
      adminItems.push({
        icon: <AdminPanelSettings />,
        text: getTypeDisplayName(adminUsersEntityType.name),
        key: `admin-user-${adminUsersEntityType.id.toString()}`,
        path: `/entities/${adminUsersEntityType.id.toString()}`,
      })
    }
    if (settingsType) {
      adminItems.push({
        icon: <Settings />,
        text: getTypeDisplayName(settingsType.name),
        key: `settings-${settingsType.id.toString()}`,
        path: `/entities/${settingsType.id.toString()}`,
      })
    }
    if (navigationType) {
      adminItems.push({
        icon: <MapIcon />,
        text: getTypeDisplayName(navigationType.name),
        key: `navigation-${navigationType.id.toString()}`,
        path: `/entities/${navigationType.id.toString()}`,
      })
    }
  }

  const showHiddenSectionsForAdmin = useMemo(() => {
    const hiddenSetting = getSettingValue('Navigation: Hidden sections visible for admins')
    if (!hiddenSetting) return true
    return normalize(hiddenSetting) === normalize('Yes')
  }, [getSettingValue, normalize])

  const assistantDebugMode = useMemo(() => {
    const setting = getSettingValue('Debug Mode')
    if (!setting) return false
    return normalize(setting) === normalize('On')
  }, [getSettingValue, normalize])

  const navigationSections: DrawerSection[] = useMemo(() => {
    if (!navigationType || !navigationFields || navigationEntries.length === 0) return []

    const typeByFullName = new Map(entityTypes.map((type) => [normalize(type.name), type]))
    const typeByTableName = new Map(entityTypes.map((type) => [normalize(getTypeDisplayName(type.name)), type]))
    const hierarchyByName = new Map(hierarchies.map((hierarchy) => [normalize(hierarchy.name), hierarchy]))
    const resolveAssetEntry = (assetConfig: AssetEntityTypeConfig, label: string) => {
      if (assetRegistryLoading || !assetEntitiesByType) return null

      const normalizedLabel = normalize(label)
      const matches: { entity: EntityWithLabelAndRelated; label: string }[] = []

      const entities = assetEntitiesByType.get(assetConfig.entityTypeId)
      if (!entities) return null
      const nameField = findFieldByName(assetConfig.entityType, 'Name')
      if (!nameField) {
        logMessage(
          `Navigation asset type "${assetConfig.entityTypeName}" requires a Name field on ${getTypeDisplayName(
            assetConfig.entityType.name
          )}.`,
          'warning',
          'Navigation'
        )
        return null
      }
      entities.forEach((entity) => {
        if (!entity) return
        const values = entity.entity.valuesByFieldId.asJsReadonlyMapView()
        const nameValue = getFieldString(values, nameField.id)
        if (!nameValue || normalize(nameValue) !== normalizedLabel) return
        const resolvedType = resolveAssetTypeForEntity(entity, assetConfig)
        if (!resolvedType) return
        matches.push({ entity, label: nameValue })
      })

      if (matches.length === 0) {
        logMessage(
          `Navigation asset "${label}" not found for type "${assetConfig.entityTypeName}".`,
          'warning',
          'Navigation'
        )
        return null
      }

      if (matches.length > 1) {
        logMessage(
          `Navigation asset "${label}" is not unique for type "${assetConfig.entityTypeName}".`,
          'error',
          'Navigation'
        )
        return null
      }

      return matches[0]
    }

    const sections: DrawerSection[] = []
    let currentTitle = 'Navigation'
    let currentItems: DrawerItem[] = []
    let currentSectionHidden = false

    const pushCurrentSection = () => {
      if (!currentItems.length) return
      if (currentSectionHidden) {
        if (!isAdminUser) {
          currentItems = []
          return
        }
        if (!showHiddenSectionsForAdmin) {
          currentItems = []
          return
        }
      }
      sections.push({
        id: `navigation-${sections.length}`,
        title: currentTitle.toUpperCase(),
        items: currentItems,
        dividerBefore: false,
      })
      currentItems = []
    }

    navigationEntries.forEach((entry) => {
      if (entry.category === 'section') {
        pushCurrentSection()
        currentTitle = entry.label.length > 0 ? entry.label : 'Navigation'
        currentSectionHidden = normalize(entry.label).startsWith('hidden ')
        return
      }

      if (entry.category === 'type') {
        const normalizedLabel = normalize(entry.label)
        const targetType = typeByFullName.get(normalizedLabel) ?? typeByTableName.get(normalizedLabel)
        if (!targetType) return
        const fallbackIcon = iconForEntityTypeKind(targetType.kind, <List />)
        currentItems.push(
          createEntityTypeItem(targetType, <DynamicMuiIcon iconName={entry.iconName} fallback={fallbackIcon} />)
        )
        return
      }

      if (entry.category === 'hierarchy') {
        const targetHierarchy = hierarchyByName.get(normalize(entry.label))
        if (!targetHierarchy) return
        currentItems.push({
          icon: <DynamicMuiIcon iconName={entry.iconName} fallback={<AccountTree />} />,
          text: targetHierarchy.name,
          key: `hierarchy-${targetHierarchy.id}`,
          path: `/hierarchies/${encodeURIComponent(targetHierarchy.id)}`,
        })
        return
      }

      if (entry.category === 'asset' && entry.assetEntityType) {
        const targetAsset = resolveAssetEntry(entry.assetEntityType, entry.label)
        if (!targetAsset) return
        currentItems.push({
          icon: <DynamicMuiIcon iconName={entry.iconName} fallback={<PermMedia />} />,
          text: targetAsset.label,
          key: `asset-${targetAsset.entity.entity.id.toString()}`,
          path: `/asset/${targetAsset.entity.entity.id.toString()}`,
        })
      }
    })

    pushCurrentSection()
    return sections
  }, [
    createEntityTypeItem,
    entityTypes,
    hierarchies,
    navigationEntries,
    navigationFields,
    navigationType,
    normalize,
    isAdminUser,
    showHiddenSectionsForAdmin,
    assetRegistryLoading,
    assetEntitiesByType,
  ])

  const sections: DrawerSection[] = []

  if (adminItems.length) {
    sections.push({
      id: 'administration',
      title: 'Administration',
      items: adminItems,
      dividerBefore: false,
    })
  }

  const contentSections = navigationFields && navigationEntries.length > 0 ? navigationSections : []
  sections.push(...contentSections)
  const assistantDrawerWidth = overlayNavigation ? 0 : 240

  if (error) {
    return (
      <SnackBarProvider>
        <Collapse in={!!error}>
          <Alert
            variant="filled"
            severity="error"
            sx={{ margin: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={async () => {
                  await entityTypeRepository.clearError()
                  await entityTypeRepository.refreshAll()
                }}
              >
                Refresh
              </Button>
            }
          >
            <AlertTitle>Failed to load entities. Please try again.</AlertTitle>
            <div>{String(error)}</div>
          </Alert>
        </Collapse>
      </SnackBarProvider>
    )
  }

  if (fetchPermissionsError) {
    return (
      <SnackBarProvider>
        <Collapse in={!!fetchPermissionsError}>
          <Alert
            variant="filled"
            severity="error"
            sx={{ margin: 2 }}
            action={
              <Button color="inherit" size="small" onClick={async () => window.location.reload()}>
                Refresh
              </Button>
            }
          >
            <AlertTitle>Failed to load permissions. Please reload the page.</AlertTitle>
            <div>{String(fetchPermissionsError)}</div>
          </Alert>
        </Collapse>
      </SnackBarProvider>
    )
  }

  return (
    <SnackBarProvider>
      <DrawerScaffold
        sections={sections}
        overlayNavigation={overlayNavigation}
        navigationOpen={navigationOpen}
        onNavigationClose={onNavigationClose}
      >
        <Assistent debugMode={assistantDebugMode} drawerWidth={assistantDrawerWidth}>
          <Outlet />
        </Assistent>
      </DrawerScaffold>
      <LoadingOverlay isOpen={isLoading} />
    </SnackBarProvider>
  )
}

export default LandingPage
